// dependencies
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { StyleSheet, css } from "aphrodite";

// components
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import Tag from "@cloudspire/legacy-resources/src/components/Tag";
import RoomRankings from "@cloudspire/legacy-resources/src/components/RoomRankings";
import RoomNewTag from "@cloudspire/legacy-resources/src/components/RoomNewTag";
import RoomAllInclusiveRate from "@cloudspire/legacy-resources/src/components/RoomAllInclusiveRate";
import RoomRateAndReviews from "@cloudspire/legacy-resources/src/components/RoomRateAndReviews";
import RoomTrophy from "@cloudspire/legacy-resources/src/components/RoomTrophy";
import Link from "@cloudspire/legacy-resources/src/components/Link";

// constants
import { HOTEL_CATEGORY_INTL_MESSAGES } from "@cloudspire/shared/constants/hotel";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";

// helpers
import {
  roomHasRanking,
  roomHasReviews,
} from "@cloudspire/legacy-resources/src/helpers/room";
import { contentGetSlugFromCategoryAlias } from "@cloudspire/legacy-resources/src/helpers/content";
import intlFormatConstantByValue from "@cloudspire/shared/utils/intl/formatConstantByValue";

const styles = StyleSheet.create({
  tag: {
    padding: "0.5rem",
    transition: "background-color 160ms linear 0ms, color 160ms linear 0ms",
  },
  tag__citybreak: {
    padding: 0,
  },
  tagCitybreak: {
    padding: "0.625rem",
  },
  newTag: {
    padding: "0.375rem",
  },
  allInclusiveRate: {
    padding: "0.375rem",
  },
});

function RoomTagList(props) {
  const {
    room,
    rate,
    reviews,
    color,
    ranking,
    categoryList,
    allInclusiveRate,
    recentlyAdded,
    baseQueryParameters,
    trophyList,
  } = props;

  const { frontRouter } = useRouters();

  const intl = useIntl();

  const configuration = useConfiguration();

  const dynamicStyles = useMemo(
    () =>
      StyleSheet.create({
        tag: {
          borderColor: color,
          color: color,
        },
        tag__animated: {
          ":hover": {
            color: "#ffffff",
            backgroundColor: color,
          },
        },
      }),
    [color]
  );

  return (
    <Row
      type="inline"
      flexWrap={true}
      gutter={0.5}
      style={{ alignItems: "center" }}
    >
      {categoryList.map((category) => {
        return (
          <Col key={category}>
            <Link
              to={generateUri({
                router: frontRouter,
                name: "App.Action.RoomListWithSlug",
                parameters: {
                  lang: intl.locale,
                  slug: contentGetSlugFromCategoryAlias({
                    contentList: configuration.channel.website.contents.list,
                    categoryAlias: category,
                  }),
                },
                query: baseQueryParameters,
              })}
            >
              <Tag
                aStyle={[
                  styles.tag,
                  dynamicStyles.tag,
                  dynamicStyles.tag__animated,
                ]}
              >
                {intlFormatConstantByValue(
                  {
                    intl,
                    messages: HOTEL_CATEGORY_INTL_MESSAGES.byValue,
                    preferredAttributes: ["label"],
                    value: category,
                  },
                  {
                    uppercaseFirst: true,
                  }
                )}
              </Tag>
            </Link>
          </Col>
        );
      })}
      {roomHasRanking({ room: { ranking } }) && (
        <Col>
          <div
            className={css(
              styles.tag,
              styles.tag__citybreak,
              dynamicStyles.tag
            )}
          >
            <RoomRankings
              room={room}
              color={color}
              aCitybreakStyle={styles.tagCitybreak}
              shouldAddSearchLink={true}
              shouldSeparateMainRatings={false}
            />
          </div>
        </Col>
      )}
      {recentlyAdded && (
        <Col>
          <RoomNewTag aStyle={styles.newTag} />
        </Col>
      )}
      {allInclusiveRate && (
        <Col>
          <RoomAllInclusiveRate aStyle={styles.allInclusiveRate} />
        </Col>
      )}
      {roomHasReviews({ room: { reviews } }) && (
        <Col>
          <a href="#reviews" style={{ textDecoration: "none" }}>
            <RoomRateAndReviews rate={rate} reviews={reviews} long={true} />
          </a>
        </Col>
      )}
      {trophyList.map((trophy) => {
        return (
          <Col key={trophy.id}>
            <RoomTrophy trophy={trophy} />
          </Col>
        );
      })}
    </Row>
  );
}

export default RoomTagList;
